import { ErrorMessage } from '@hookform/error-message'
import get from 'lodash/get'
import type { Control } from 'react-hook-form'
import { useController, useFormState } from 'react-hook-form'

import { CustomFieldId, useFields } from '@graphql/hooks/field'
import type { ContactInfo } from '@lib/store/waitlist/WaitlistStore'
import { TEXT } from '@lib/utils/text'
import { validateTelephoneInput } from '@shared/util/phone'

/**
 * Hooks up the field configuration from Contentful to react-hook-form's `register`.
 */
export const useSchoolRequestFields = (control: Control<ContactInfo>) => {
    const fields = useFields({
        phone: CustomFieldId.SchoolRequestPhone,
        email: CustomFieldId.SchoolRequestEmail,
        name: CustomFieldId.SchoolRequestFullName,
        school: CustomFieldId.SchoolRequestSchool,
        cityState: CustomFieldId.SchoolRequestCityState,
        facultyAdvisor: CustomFieldId.SchoolRequestFacultyAdvisor,
    })

    const { isSubmitting, errors, submitCount } = useFormState({ control })

    const phoneController = useController({
        control,
        name: 'phone',
        rules: {
            required: {
                value: true,
                message: fields.phone.errorTexts.required,
            },
            validate: async (phone) => {
                if (typeof phone !== 'string') return false

                const isValid = await validateTelephoneInput(phone)
                return isValid || fields.phone.errorTexts.validate
            },
        },
    })

    const register = (key: keyof typeof fields) =>
        ({
            ...(key === 'phone'
                ? phoneController.field
                : control.register(key, {
                      required: {
                          value: key !== 'facultyAdvisor' ? true : false,
                          message:
                              fields[key].errorTexts.required ?? TEXT.required,
                      },
                  })),
            disabled: isSubmitting,
            error: <ErrorMessage errors={errors} name={key} />,
            label: fields[key].label,
            placeholder: fields[key].placeholder,
            preset: 'material',
            size: 'medium',
            valid: !get(errors, key),
            validationCount: submitCount,
        }) as const

    return { fields, register }
}
